import React from 'react'

import {useAccountCategories} from '@product-web/api-deimos/account-categories'
import {useCompanySettings} from '@product-web/api-deimos/company-settings'
import {useEmployeeSummary} from '@product-web/api-deimos/employee'
import {useHasAWalletLoad} from '@product-web-features/funds-management/use-has-a-wallet-load'
import {usePleoGuideMilestones} from '@product-web-features/onboarding/pleo-guide/use-pleo-guide-milestones'

export const useSetPleoGuideMilestones = () => {
    useSetFirstPurchaseMilestone()
    useSetInviteTeamMilestone()
    useSetAccountingSetupMilestone()
}

const useSetFirstPurchaseMilestone = () => {
    const {data: milestones, mutate: setPleoGuideMilestones} = usePleoGuideMilestones()
    const {hasAWalletLoad} = useHasAWalletLoad()

    React.useEffect(() => {
        // If milestones are loading (or failed to load), or if the milestone is already done, do nothing
        if (!milestones || milestones.firstPurchase) {
            return
        }

        // If user has already done a topup elsewhere, set the card to done.
        if (hasAWalletLoad) {
            setPleoGuideMilestones({
                accountingSetup: !!milestones.accountingSetup,
                inviteTeam: !!milestones.inviteTeam,
                firstPurchase: true,
            })
        }
    }, [hasAWalletLoad, milestones, setPleoGuideMilestones])
}

const useSetInviteTeamMilestone = () => {
    const {data: milestones, mutate: setPleoGuideMilestones} = usePleoGuideMilestones()
    const {data: employeesCount} = useEmployeeSummary()

    const nonOwnerEmployeesCount = employeesCount?.member ?? 0

    React.useEffect(() => {
        // If milestones are loading (or failed to load), or if the milestone is already done, do nothing
        if (!milestones || milestones.inviteTeam) {
            return
        }

        if (nonOwnerEmployeesCount > 0) {
            setPleoGuideMilestones({
                firstPurchase: !!milestones.firstPurchase,
                accountingSetup: !!milestones.accountingSetup,
                inviteTeam: true,
            })
        }
    }, [nonOwnerEmployeesCount, milestones, setPleoGuideMilestones])
}

const useSetAccountingSetupMilestone = () => {
    const {data: milestones, mutate: setPleoGuideMilestones} = usePleoGuideMilestones()
    const {data: accountCategories} = useAccountCategories()
    const preferences = useCompanySettings()

    const accountingSystem = preferences?.data?.accounting?.system
    const isAccountingSystemConnected = Boolean(accountingSystem && accountingSystem !== 'none')
    const hasAValidCategory = accountCategories?.some((accountCategory) =>
        // A category exists that has an associated account number
        accountCategory.accounts?.some((account) => account.accountNumber),
    )

    React.useEffect(() => {
        // If milestones are loading (or failed to load), or if the milestone is already done, do nothing
        if (!milestones || milestones.accountingSetup) {
            return
        }

        if (isAccountingSystemConnected && hasAValidCategory) {
            setPleoGuideMilestones({
                firstPurchase: !!milestones.firstPurchase,
                inviteTeam: !!milestones.inviteTeam,
                accountingSetup: true,
            })
        }
    }, [hasAValidCategory, isAccountingSystemConnected, milestones, setPleoGuideMilestones])
}
